// useAaSmoothTip — 困りごとのコツ「線がギザギザに見える → なめらか (AA) にする」の出し分け。
// (docs/_wip/aa-default-and-trouble-tips-2026-10-27.md §2)
//
// RoomLayout が呼ぶ。返す `onOwnStrokeEnd` を CanvasStage に渡すと、自分の線を描き終えるたびに
// 条件 (domain/troubleTips.ts) を判定して数え、規定本数に達したペンアップ後に `visible` を true にする。
//   - 「自分で選んだ値なし かつ 実際の AA がなし」を判定の瞬間に localStorage から読んで見る
//     (自分で選んだ人・端末の既定が「あり」の人には出さない)。
//   - 入室時モーダルなど他のダイアログ (aria-modal) が開いている間は出さない。閉じたあとの
//     次の (条件を満たす) ペンアップで出る。
//   - 既読 (1 端末 1 回) は閉じた時点で記録。storage 不可でもこの入室中は 1 回だけ。

import type { StrokePen } from '@oekaki/protocol';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Transport } from '../../application/ports/Transport.js';
import { reportTip } from '../../application/tipAnalytics.js';
import { AA_SMOOTH_TIP } from '../../domain/troubleTips.js';
import { createAaPreferenceStore, resolveEffectiveAa } from '../../infrastructure/aaPreference.js';
import type { CanvasDrawingEngine } from '../../infrastructure/drawing/CanvasDrawingEngine.js';
import { createTipSeenStore } from '../../infrastructure/tipSeen.js';

export const isModalDialogOpen = (): boolean =>
  typeof document !== 'undefined' && document.querySelector('[aria-modal="true"]') !== null;

export interface UseAaSmoothTipInput {
  readonly engine: Pick<CanvasDrawingEngine, 'getZoom'> | null;
  /** 「なめらかにする」で AA を明示設定 on にする (chrome.onAaChange、その場で反映)。 */
  readonly onAaChange: (next: boolean) => void;
  /** カードの表示・操作の計装 (analytics.tip) の送り先。未指定なら送らない。 */
  readonly transport?: Transport;
  /** テスト注入用。既定は DOM の aria-modal を見る。 */
  readonly isModalOpen?: () => boolean;
}

export interface AaSmoothTip {
  readonly visible: boolean;
  readonly onOwnStrokeEnd: (info: { readonly pen: StrokePen; readonly width: number }) => void;
  /** 「なめらかにする」: AA on + 既読 + 閉じる。 */
  readonly accept: () => void;
  /** 「このままでいい」: 既読 + 閉じる。 */
  readonly dismiss: () => void;
  /** 「くわしく」を開いた (計装のみ。カードは閉じない)。 */
  readonly detail: () => void;
}

export const useAaSmoothTip = (input: UseAaSmoothTipInput): AaSmoothTip => {
  const { engine, onAaChange, transport } = input;
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const report = useCallback((action: 'shown' | 'accept' | 'dismiss' | 'detail'): void => {
    if (transportRef.current) reportTip(transportRef.current, AA_SMOOTH_TIP.id, action);
  }, []);
  const isModalOpen = input.isModalOpen ?? isModalDialogOpen;
  const aaStore = useMemo(() => createAaPreferenceStore(), []);
  const seenStore = useMemo(() => createTipSeenStore(), []);
  const [visible, setVisible] = useState(false);
  const countRef = useRef(0);
  // storage 不可でもこの入室中は 1 回だけにする。
  const doneRef = useRef(false);
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const isModalOpenRef = useRef(isModalOpen);
  isModalOpenRef.current = isModalOpen;

  const onOwnStrokeEnd = useCallback(
    (info: { readonly pen: StrokePen; readonly width: number }): void => {
      if (doneRef.current) return;
      if (seenStore.hasSeen(AA_SMOOTH_TIP.id)) {
        doneRef.current = true;
        return;
      }
      const zoom = engineRef.current?.getZoom() ?? 1;
      // 解決順: 自分で選んだ値 → 端末の既定 → off。端末の既定が「あり」の人は実際に
      // 「あり」で描いているので出さない。
      const aaExplicit = aaStore.getExplicit();
      const aaEffective = resolveEffectiveAa(aaExplicit, aaStore.getDeviceDefault());
      if (!AA_SMOOTH_TIP.counts({ aaExplicit, aaEffective, stroke: { ...info, zoom } })) {
        return;
      }
      countRef.current += 1;
      if (countRef.current < AA_SMOOTH_TIP.strokesNeeded) return;
      if (isModalOpenRef.current()) return;
      // doneRef で 1 回きり (再レンダーで shown が重複しない)。
      doneRef.current = true;
      setVisible(true);
      report('shown');
    },
    [aaStore, seenStore, report],
  );

  const close = useCallback((): void => {
    seenStore.markSeen(AA_SMOOTH_TIP.id);
    setVisible(false);
  }, [seenStore]);
  const accept = useCallback((): void => {
    report('accept');
    onAaChange(true);
    close();
  }, [onAaChange, close, report]);
  const dismiss = useCallback((): void => {
    report('dismiss');
    close();
  }, [close, report]);
  const detail = useCallback((): void => report('detail'), [report]);

  return { visible, onOwnStrokeEnd, accept, dismiss, detail };
};
